import {
  Box,
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactElement, MouseEvent, ReactNode } from "react";
import { cloneElement } from "react";

export type TableActionTone = "primary" | "success" | "error" | "neutral";

interface TableActionButtonProps {
  icon: ReactElement;
  label: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  tone?: TableActionTone;
  /** true = ícone compacto (tabela desktop); false = pill com rótulo (mobile). */
  compact?: boolean;
  disabled?: boolean;
}

function resolveTone(theme: ReturnType<typeof useTheme>, tone: TableActionTone) {
  switch (tone) {
    case "success":
      return {
        main: theme.palette.success.main,
        dark: theme.palette.success.dark,
      };
    case "error":
      return {
        main: theme.palette.error.main,
        dark: theme.palette.error.dark,
      };
    case "neutral":
      return {
        main: theme.palette.text.secondary,
        dark: theme.palette.text.primary,
      };
    default:
      return {
        main: theme.palette.primary.main,
        dark: theme.palette.primary.dark,
      };
  }
}

/**
 * Botão de ação padronizado para tabelas e cards mobile.
 */
export function TableActionButton({
  icon,
  label,
  onClick,
  tone = "primary",
  compact = false,
  disabled = false,
}: TableActionButtonProps) {
  const theme = useTheme();
  const colors = resolveTone(theme, tone);

  if (compact) {
    return (
      <Tooltip title={label} arrow placement="top">
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={onClick}
            aria-label={label}
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              color: disabled ? "action.disabled" : colors.main,
              bgcolor: disabled
                ? "action.hover"
                : alpha(colors.main, 0.08),
              border: "1px solid",
              borderColor: disabled
                ? "divider"
                : alpha(colors.main, 0.18),
              transition:
                "background-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease",
              "&:hover": {
                bgcolor: alpha(colors.main, 0.14),
                boxShadow: `0 2px 8px ${alpha(colors.main, 0.18)}`,
              },
              "&:active": { transform: "scale(0.94)" },
              "& svg": { fontSize: "1.05rem" },
            }}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 44,
        borderRadius: 2.5,
        px: 0.5,
        py: 0.65,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.35,
        color: disabled ? "action.disabled" : colors.main,
        bgcolor: disabled ? "action.hover" : alpha(colors.main, 0.07),
        border: "none",
        borderColor: disabled ? "divider" : alpha(colors.main, 0.2),
        transition:
          "background-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease",
        "&:hover": {
          bgcolor: alpha(colors.main, 0.12),
          boxShadow: `0 2px 10px ${alpha(colors.main, 0.12)}`,
        },
        "&:active": { transform: "scale(0.98)" },
      }}
    >
      {cloneElement(icon, {
        sx: { fontSize: 18, ...(icon.props as { sx?: object }).sx },
      })}
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          fontSize: "0.625rem",
          letterSpacing: "0.01em",
          lineHeight: 1.2,
          textAlign: "center",
          whiteSpace: "normal",
          wordBreak: "break-word",
          width: "100%",
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

/** Agrupa ações em linha — grid de largura total no mobile. */
export function TableActionGroup({
  children,
  fullWidth = false,
}: {
  children: ReactNode;
  fullWidth?: boolean;
}) {
  if (fullWidth) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          width: "100%",
          minWidth: "100%",
          flex: 1,
          "& > *": {
            flex: "1 1 0",
            minWidth: 0,
            maxWidth: "100%",
          },
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        width: "auto",
      }}
    >
      {children}
    </Box>
  );
}